import React from "react";
import { FaSpinner } from "react-icons/fa";
import "./responsiveTable.css";

/**
 * ResponsiveTable — reusable report table with automatic mobile card layout.
 *
 * Props:
 *   columns      {key, label, render?, align?, width?}[]  — defines table columns
 *   data         object[]            — row data array
 *   rowKey       (row,i) => key      — optional: unique key per row (defaults to index)
 *   footer       object | null       — if provided, renders a totals row; values keyed same as columns
 *   footerLabel  string              — label for the first cell of footer row (default "Total")
 *   loading      bool
 *   emptyMessage string | ReactNode
 *   renderCard   (row, index) => ReactNode  — mobile card renderer (required for best UX)
 *   renderTotalsCard () => ReactNode        — optional mobile totals card renderer
 *   maxHeight    string              — CSS max-height for desktop scroll container
 */
const ResponsiveTable = ({
  columns = [],
  data = [],
  rowKey,
  footer = null,
  footerLabel = "Total",
  loading = false,
  emptyMessage = "No data found.",
  renderCard,
  renderTotalsCard,
  maxHeight = "calc(100vh - 300px)",
}) => {
  if (loading) {
    return (
      <div className="rsp-loading">
        <FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: 22 }} />
        <span>Loading…</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <div className="rsp-empty">{emptyMessage}</div>;
  }

  const getKey = (row, i) => (rowKey ? rowKey(row, i) : i);

  /* ── Default auto-card: stacks all columns as label: value pairs ── */
  const DefaultCard = ({ row, index }) => (
    <div className="rsp-card">
      <div className="rsp-card-header">
        <span className="rsp-card-title">
          {columns[0]?.render
            ? columns[0].render(row[columns[0].key], row, index)
            : row[columns[0]?.key]}
        </span>
        <span className="rsp-card-badge">#{index + 1}</span>
      </div>
      <div className="rsp-card-body">
        <div className="rsp-stat-grid rsp-stat-grid-2">
          {columns.slice(1).map((col) => (
            <div className="rsp-stat" key={col.key}>
              <span className="rsp-stat-label">{col.label}</span>
              <span className="rsp-stat-value">
                {col.render ? col.render(row[col.key], row, index) : row[col.key]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="rsp-wrapper">

      {/* ══ DESKTOP + TABLET: standard table ══ */}
      <div
        className="rsp-table-scroll"
        style={{ "--rsp-max-height": maxHeight }}
      >
        <table className="rsp-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.align || "left",
                    width: col.width || "auto",
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={getKey(row, i)}>
                {columns.map((col) => (
                  <td key={col.key} style={{ textAlign: col.align || "left" }}>
                    {col.render
                      ? col.render(row[col.key], row, i)
                      : (row[col.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer && (
            <tfoot>
              <tr>
                {columns.map((col, i) => (
                  <td key={col.key} style={{ textAlign: col.align || "left" }}>
                    {i === 0
                      ? <strong>{footerLabel}</strong>
                      : footer[col.key] !== undefined
                        ? <strong>{footer[col.key]}</strong>
                        : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ══ MOBILE: card layout ══ */}
      <div className="rsp-cards">
        {data.map((row, i) =>
          renderCard
            ? <React.Fragment key={getKey(row, i)}>{renderCard(row, i)}</React.Fragment>
            : <DefaultCard key={getKey(row, i)} row={row} index={i} />
        )}
        {footer && (
          renderTotalsCard
            ? renderTotalsCard()
            : <DefaultTotalsCard columns={columns} footer={footer} label={footerLabel} />
        )}
      </div>

    </div>
  );
};

/* Default mobile totals card — shows all footer values in a grid */
const DefaultTotalsCard = ({ columns, footer, label }) => (
  <div className="rsp-totals-card">
    <div className="rsp-totals-title">{label}</div>
    <div className="rsp-totals-grid rsp-totals-grid-2">
      {columns.filter((col, i) => i > 0 && footer[col.key] !== undefined).map((col) => (
        <div className="rsp-total-stat" key={col.key}>
          <span className="rsp-total-stat-label">{col.label}</span>
          <span className="rsp-total-stat-value">{footer[col.key]}</span>
        </div>
      ))}
    </div>
  </div>
);

export default ResponsiveTable;
